import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, ChevronDown, LoaderCircle } from "lucide-react";
import { Reveal, SectionHeading, WhatsAppIcon } from "./Reveal";
import { INSTITUTE, SOCIALS, WA_DEFAULT, waLink, API_BASE } from "@/lib/site";

const COURSE_OPTIONS = ["Graphic Design", "Photoshop", "Video Editing", "Other"];
const MAP_SRC =
  "https://www.google.com/maps?q=Bharwari%2C%20Kaushambi%2C%20Uttar%20Pradesh%20212201&output=embed";

const initialState = { name: "", phone: "", course: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, course: e.detail }));
    window.addEventListener("mmi:enquire", handler);
    return () => window.removeEventListener("mmi:enquire", handler);
  }, []);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your full name";
    const digits = form.phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "").replace(/^0(?=\d{10}$)/, "");
    if (digits.length !== 10) er.phone = "Enter a valid 10-digit phone number";
    if (!form.course) er.course = "Please select a course";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong. Please try again.");
      toast.success("Enquiry received! Opening WhatsApp…", { description: "We usually reply very quickly." });
      window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
      setForm((f) => ({ ...initialState, course: f.course }));
    } catch (err) {
      toast.error(err.message || "Could not submit enquiry. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-gray-500 outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all duration-300";

  return (
    <section id="contact" className="relative py-20 md:py-28" data-testid="contact-section">
      <div className="absolute left-0 bottom-0 -z-10 h-[400px] w-[400px] rounded-full bg-amber-500/[0.05] blur-[130px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Contact"
          title="Let's Connect"
          sub="Have a question about courses or admissions? Reach out — we're happy to help."
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-8 lg:gap-10">
          {/* Info side */}
          <div className="space-y-4">
            {[
              {
                icon: MapPin,
                label: "Location",
                value: INSTITUTE.address,
                href: "https://www.google.com/maps?q=Bharwari,+Kaushambi,+Uttar+Pradesh+212201",
                testid: "contact-location",
              },
              {
                icon: Phone,
                label: "Phone",
                value: INSTITUTE.phoneDisplay,
                href: `tel:${INSTITUTE.phoneTel}`,
                testid: "contact-phone",
              },
              {
                icon: Mail,
                label: "Email",
                value: INSTITUTE.email,
                href: `mailto:${INSTITUTE.email}`,
                testid: "contact-email",
              },
            ].map((item, i) => (
              <Reveal key={item.label} delay={i * 0.08}>
                <a
                  href={item.href}
                  target={item.label === "Location" ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="group flex items-start gap-4 rounded-3xl border border-white/[0.07] bg-obsidian-card/80 p-6 hover:border-amber-500/35 transition-all duration-300"
                  data-testid={item.testid}
                >
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-500/10 border border-amber-500/25 text-amber-500 group-hover:bg-amber-500 group-hover:text-obsidian transition-all duration-300">
                    <item.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
                      {item.label}
                    </span>
                    <span className="mt-1.5 block text-sm sm:text-[15px] text-gray-200 leading-relaxed break-words">
                      {item.value}
                    </span>
                  </span>
                </a>
              </Reveal>
            ))}

            <Reveal delay={0.28}>
              <div className="grid grid-cols-3 gap-3" data-testid="contact-actions">
                <a
                  href={`tel:${INSTITUTE.phoneTel}`}
                  className="flex items-center justify-center gap-2 rounded-full bg-amber-500 px-4 py-3.5 text-xs sm:text-sm font-semibold text-obsidian hover:bg-amber-400 hover:shadow-glow-amber-sm transition-all duration-300"
                  data-testid="contact-call-button"
                >
                  <Phone className="h-4 w-4" /> Call Now
                </a>
                <a
                  href={WA_DEFAULT}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/[0.06] px-4 py-3.5 text-xs sm:text-sm font-medium text-emerald-400 hover:bg-emerald-500/15 transition-all duration-300"
                  data-testid="contact-whatsapp-button"
                >
                  <WhatsAppIcon className="h-4 w-4" /> WhatsApp
                </a>
                <a
                  href={`mailto:${INSTITUTE.email}`}
                  className="flex items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-3.5 text-xs sm:text-sm font-medium text-gray-200 hover:border-amber-500/60 hover:text-amber-400 transition-all duration-300"
                  data-testid="contact-email-button"
                >
                  <Send className="h-4 w-4" /> Email
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.34}>
              <div className="flex items-center gap-3 pt-2" data-testid="contact-socials">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Follow</span>
                <span className="h-px flex-1 bg-white/[0.07]" />
                {SOCIALS.map((s) => (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${INSTITUTE.name} on ${s.name}`}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/12 text-gray-400 hover:border-amber-500/60 hover:text-amber-400 hover:-translate-y-0.5 transition-all duration-300"
                    data-testid={`contact-social-${s.name.toLowerCase()}`}
                  >
                    {s.name === "Instagram" && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                        <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
                        <circle cx="12" cy="12" r="4.2" />
                        <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
                      </svg>
                    )}
                    {s.name === "Facebook" && (
                      <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                        <path d="M13.5 21v-7.5h2.52l.48-3H13.5V8.58c0-.87.24-1.46 1.5-1.46h1.6V4.42c-.28-.04-1.23-.12-2.34-.12-2.32 0-3.9 1.41-3.9 4v2.2H7.5v3h2.86V21h3.14Z" />
                      </svg>
                    )}
                    {s.name === "YouTube" && (
                      <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                        <path d="M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.28 5 12 5 12 5s-6.28 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26.2 26.2 0 0 0 2 12a26.2 26.2 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.76 1.77C5.72 19 12 19 12 19s6.28 0 7.84-.43a2.5 2.5 0 0 0 1.76-1.77A26.2 26.2 0 0 0 22 12a26.2 26.2 0 0 0-.4-4.8ZM10 15.2V8.8L15.5 12 10 15.2Z" />
                      </svg>
                    )}
                  </a>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Form side */}
          <Reveal delay={0.12} y={36}>
            <form
              onSubmit={onSubmit}
              noValidate
              className="rounded-3xl border border-white/[0.08] bg-obsidian-card/90 p-6 sm:p-8 space-y-5"
              data-testid="enquiry-form"
            >
              <h3 className="font-display text-lg font-bold text-white">Admission Enquiry</h3>

              <div>
                <label htmlFor="name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400">
                  Full Name *
                </label>
                <input
                  id="name"
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Enter your full name"
                  className={inputCls}
                  data-testid="enquiry-name-input"
                />
                {errors.name && (
                  <p className="mt-1.5 text-xs text-red-400" data-testid="enquiry-name-error">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400">
                  Phone Number *
                </label>
                <input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Your 10-digit mobile number"
                  className={inputCls}
                  data-testid="enquiry-phone-input"
                />
                {errors.phone && (
                  <p className="mt-1.5 text-xs text-red-400" data-testid="enquiry-phone-error">{errors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="course" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400">
                  Course Interested In *
                </label>
                <div className="relative">
                  <select
                    id="course"
                    value={form.course}
                    onChange={set("course")}
                    className={`${inputCls} appearance-none pr-11 ${!form.course ? "text-gray-500" : ""}`}
                    data-testid="enquiry-course-select"
                  >
                    <option value="" disabled className="bg-obsidian-card">
                      Select a course
                    </option>
                    {COURSE_OPTIONS.map((c) => (
                      <option key={c} value={c} className="bg-obsidian-card text-white">
                        {c}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                </div>
                {errors.course && (
                  <p className="mt-1.5 text-xs text-red-400" data-testid="enquiry-course-error">{errors.course}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-gray-400">
                  Message
                </label>
                <textarea
                  id="message"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us what you'd like to learn…"
                  className={`${inputCls} resize-none`}
                  data-testid="enquiry-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 px-8 py-4 text-sm sm:text-base font-semibold text-obsidian hover:bg-amber-400 hover:shadow-glow-amber transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="enquiry-submit-button"
              >
                {submitting ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Submit Enquiry
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>

              <p className="text-center text-[11px] text-gray-500">
                Your enquiry opens WhatsApp with everything pre-filled — just hit send.
              </p>
            </form>
          </Reveal>
        </div>

        {/* Map */}
        <Reveal delay={0.1}>
          <div className="relative mt-10 overflow-hidden rounded-3xl border border-white/[0.08]" data-testid="contact-map">
            <iframe
              title="Motion Mind Institute location — Bharwari, Kaushambi"
              src={MAP_SRC}
              className="h-72 sm:h-80 w-full grayscale invert-[0.92] hue-rotate-180 contrast-[0.9]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <div className="absolute left-4 bottom-4 rounded-2xl glass px-5 py-3.5">
              <p className="flex items-center gap-2 text-sm text-white">
                <MapPin className="h-4 w-4 text-amber-500" />
                {INSTITUTE.addressShort}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
